import * as React from 'react';
import { useTranslation } from 'react-i18next';

/** Her story opens by itself after this long on screen (the bar fills up). */
export const AUTO_MEET_MS = 5000;

const STYLES = `
@keyframes autoMeetCountdown { from { width: 0; } to { width: 100%; } }
.auto-meet-bar { width: 0; animation: autoMeetCountdown ${AUTO_MEET_MS}ms linear forwards; }
.auto-meet-sr {
  position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px;
  overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; border: 0;
}
@media (prefers-reduced-motion: reduce) { .auto-meet-bar { display: none; } }
`;

function queryReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}

/**
 * AutoMeetCountdown — "her story opens by itself in 5 s": a thin bar that
 * fills along the bottom edge of its (positioned) parent, then calls
 * `onFire`. It only counts while the parent is on screen, so nothing opens
 * while the student is scrolled elsewhere. No countdown with reduced motion.
 * Render it only while auto-opening is allowed; unmounting cancels it.
 */
export function AutoMeetCountdown({
  onFire,
  color,
  height = 4,
  threshold = 0.5,
}: {
  onFire: () => void;
  color: string;
  height?: number;
  /** share of the parent that must be visible for the countdown to run */
  threshold?: number;
}) {
  const { t } = useTranslation('p2e');
  const anchorRef = React.useRef<HTMLSpanElement | null>(null);
  const [inView, setInView] = React.useState(false);
  const [reduced] = React.useState(queryReducedMotion);

  React.useEffect(() => {
    const target = anchorRef.current?.parentElement;
    if (!target || typeof IntersectionObserver === 'undefined') return;
    const observer = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [threshold]);

  const counting = inView && !reduced;
  // Latest handler without restarting the countdown on every render.
  const onFireRef = React.useRef(onFire);
  onFireRef.current = onFire;
  React.useEffect(() => {
    if (!counting) return;
    const timer = window.setTimeout(() => onFireRef.current(), AUTO_MEET_MS);
    return () => clearTimeout(timer);
  }, [counting]);

  return (
    // display: contents — no box of its own, so it never takes a grid/flex
    // slot in the parent; the bar positions against the parent.
    <span ref={anchorRef} style={{ display: 'contents' }}>
      <style dangerouslySetInnerHTML={{ __html: STYLES }} />
      {counting && (
        <>
          <span
            aria-hidden
            className="auto-meet-bar"
            data-testid="auto-meet-countdown"
            style={{
              position: 'absolute',
              left: 0,
              bottom: 0,
              height,
              background: color,
              opacity: 0.85,
              zIndex: 5,
            }}
          />
          <span className="auto-meet-sr" role="status">
            {t('story.banner.autoOpen')}
          </span>
        </>
      )}
    </span>
  );
}
